import { api } from '@/api';
import { onboardingQuery } from '@/features/user/queries';
import { updateUserProfile } from '@/features/user/api';
import type { Locale } from '@/features/i18n/constants';
import { saveLocale, t } from '@/features/i18n/utils';
import { toastManager } from '@/ui/components/toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

export function useSwitchOrganization() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (organizationId: string) =>
      api.put(`v1/users/logged/organizations/${organizationId}/default`),
    onSuccess: () => {
      navigate({ to: '/' });
      return queryClient.invalidateQueries(onboardingQuery());
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      firstName: string;
      lastName: string;
      systemLanguage: Locale;
      defaultTranslationLanguage: Locale;
    }) => updateUserProfile(payload),
    onSuccess: (_, payload) => {
      const previous = queryClient.getQueryData(onboardingQuery().queryKey)?.userData;
      if (payload.systemLanguage !== previous?.systemLanguage) {
        saveLocale(payload.systemLanguage);
        return window.location.reload();
      }
      queryClient.setQueryData(
        onboardingQuery().queryKey,
        (old) => old && { ...old, userData: { ...old.userData, ...payload } },
      );
      toastManager.add({
        type: 'success',
        title: t({ en: 'User profile updated!', nb: 'Brukerprofil oppdatert!' }),
      });
    },
  });
}
